import { useMemo, useState, type FormEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api, errorMessage } from "../../api/client";
import { useClients, useRoles, useUsers } from "../../api/hooks";
import type { RoleDef, UserRow } from "../../api/types";
import { Chip, Drawer, Empty, Icon, Kpi, PageHead, Segmented, Spinner, useToast, type Tone } from "../../components/ui";
import { ROLE_LABEL, useAuth } from "../../lib/auth";
import { dateTime } from "../../lib/format";

const ROLE_TONE: Record<string, Tone> = {
  SUPER_ADMIN: "red", ADMIN: "purple", MANAGER: "blue", OPERATOR: "green", CLIENT: "orange",
};

const PERM_LABEL: Record<string, string> = {
  "data.read": "Consulter la Master Data", "data.write": "Modifier la Master Data", "data.import": "Importer",
  "data.export": "Exporter", "data.merge": "Traiter les doublons", "fields.manage": "Gérer les champs",
  "mission.read": "Voir les missions", "mission.write": "Piloter les missions", "lead.read": "Voir ses leads",
  "lead.write": "Mettre à jour ses leads", "prospection.execute": "Prospecter", "results.read": "Voir les résultats",
  "results.write": "Valider les résultats", "reports.read": "Voir les rapports", "users.read": "Voir les utilisateurs",
  "users.write": "Gérer les utilisateurs", "clients.read": "Voir les clients", "clients.write": "Gérer les clients",
  "audit.read": "Journal d'audit", "settings.write": "Configuration",
};

type Status = "active" | "disabled" | "all";
type Editing = { mode: "create" } | { mode: "edit"; user: UserRow } | null;

/** Mot de passe temporaire : affiché UNE seule fois, jamais stocké côté client. */
function SecretBox({ email, password, onClose }: { email: string; password: string; onClose: () => void }) {
  const toast = useToast();
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(password);
      toast("Mot de passe copié");
    } catch {
      toast("Copie impossible : sélectionnez le texte manuellement", true);
    }
  };
  return (
    <div className="tile" role="alert" style={{ borderColor: "var(--orange-b, var(--hair))" }}>
      <b>Mot de passe temporaire pour {email}</b>
      <div className="row">
        <code className="mono input" style={{ flex: 1, display: "flex", alignItems: "center", userSelect: "all" }} aria-label="Mot de passe temporaire">{password}</code>
        <button className="btn" type="button" onClick={copy}><Icon name="copy" />Copier</button>
      </div>
      <span className="muted" style={{ fontSize: 13 }}>
        Il ne sera plus affiché. Transmettez-le par un canal privé ; l'utilisateur devra se reconnecter avec.
      </span>
      <div className="row"><button className="btn sm" type="button" onClick={onClose}>J'ai noté le mot de passe</button></div>
    </div>
  );
}

function UserForm({ editing, roles, onSaved, onSecret }: {
  editing: Exclude<Editing, null>; roles: RoleDef[]; onSaved: (u?: UserRow) => void;
  onSecret: (s: { email: string; password: string }) => void;
}) {
  const me = useAuth();
  const toast = useToast();
  const qc = useQueryClient();
  const u = editing.mode === "edit" ? editing.user : null;
  const [email, setEmail] = useState(u?.email ?? "");
  const [name, setName] = useState(u?.name ?? "");
  const [role, setRole] = useState(u?.role ?? "OPERATOR");
  const [operatorCode, setOperatorCode] = useState(u?.operator_code ?? "");
  const [clientId, setClientId] = useState<string>(u?.client_id ? String(u.client_id) : "");
  const [pwdMode, setPwdMode] = useState<"auto" | "manual">("auto");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const clients = useClients(role === "CLIENT");
  const self = u?.id === me.user_id;
  const assignable = roles.filter((r) => r.assignable || r.code === u?.role);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (pwdMode === "manual" && !u && password.length < 12) {
      setError("Le mot de passe doit contenir au moins 12 caractères.");
      return;
    }
    setBusy(true);
    const body = {
      email: email.trim(), name: name.trim(), role, operator_code: operatorCode.trim() || null,
      client_id: role === "CLIENT" && clientId ? Number(clientId) : null,
    };
    try {
      if (u) {
        const payload: Record<string, unknown> = { ...body };
        if (self || role === u.role) delete payload.role;
        const out = await api<UserRow>(`/users/${u.id}`, { method: "PATCH", json: payload });
        toast(role !== u.role ? "Utilisateur mis à jour · reconnexion requise pour appliquer le nouveau rôle" : "Utilisateur mis à jour");
        onSaved(out);
      } else {
        const out = await api<{ id: number; temporary_password?: string }>("/users", {
          json: { ...body, password: pwdMode === "manual" ? password : null },
        });
        toast(`Utilisateur ${body.email} créé`);
        if (out.temporary_password) onSecret({ email: body.email, password: out.temporary_password });
        onSaved();
      }
      qc.invalidateQueries({ queryKey: ["users"] });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const roleDef = roles.find((r) => r.code === role);
  return (
    <form className="stack" style={{ gap: 14 }} onSubmit={submit} aria-label={u ? "Modifier l'utilisateur" : "Nouvel utilisateur"}>
      <label className="field">Email
        <input className="input" type="email" required autoComplete="off" value={email} onChange={(e) => setEmail(e.target.value)} />
      </label>
      <label className="field">Nom complet
        <input className="input" required maxLength={200} value={name} onChange={(e) => setName(e.target.value)} />
      </label>
      <label className="field">Rôle
        <select className="select" value={role ?? ""} disabled={self} onChange={(e) => setRole(e.target.value)}>
          {assignable.map((r) => <option key={r.code} value={r.code}>{r.label}</option>)}
        </select>
        {self && <span className="faint" style={{ fontWeight: 400 }}>Vous ne pouvez pas modifier votre propre rôle.</span>}
      </label>
      {roleDef && (
        <div className="row" aria-label="Permissions accordées">
          {roleDef.permissions.map((p) => <Chip key={p} tone="gray">{PERM_LABEL[p] ?? p}</Chip>)}
        </div>
      )}
      {role === "CLIENT" && (
        <label className="field">Client rattaché
          <select className="select" required value={clientId} onChange={(e) => setClientId(e.target.value)}>
            <option value="">— Choisir un client —</option>
            {(clients.data ?? []).map((c) => <option key={c.id} value={c.id}>{c.name} ({c.code})</option>)}
          </select>
          {clients.data?.length === 0 && <span className="error">Aucun client : créez d'abord un client.</span>}
        </label>
      )}
      <label className="field">Code opérateur <span className="faint" style={{ fontWeight: 400 }}>(optionnel, ex. OP-17)</span>
        <input className="input" maxLength={32} value={operatorCode} onChange={(e) => setOperatorCode(e.target.value)} />
      </label>
      {!u && (
        <div className="field">Mot de passe
          <Segmented label="Mot de passe" value={pwdMode} onChange={setPwdMode} options={[["auto", "Générer un mot de passe temporaire"], ["manual", "Saisir"]]} />
          {pwdMode === "manual" && (
            <input className="input" type="password" autoComplete="new-password" minLength={12} placeholder="12 caractères minimum"
              aria-label="Nouveau mot de passe" value={password} onChange={(e) => setPassword(e.target.value)} />
          )}
        </div>
      )}
      {error && <span className="error" role="alert">{error}</span>}
      <div className="row">
        <button className="btn primary" type="submit" disabled={busy}>{busy ? "Enregistrement…" : u ? "Enregistrer" : "Créer l'utilisateur"}</button>
      </div>
    </form>
  );
}

function DangerZone({ user, onChanged, onSecret }: {
  user: UserRow; onChanged: (u?: UserRow, closed?: boolean) => void; onSecret: (s: { email: string; password: string }) => void;
}) {
  const me = useAuth();
  const toast = useToast();
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const self = user.id === me.user_id;

  const run = async (label: string, confirmText: string | null, fn: () => Promise<void>) => {
    if (confirmText && !window.confirm(confirmText)) return;
    setBusy(true);
    try {
      await fn();
      qc.invalidateQueries({ queryKey: ["users"] });
    } catch (e) {
      toast(`${label} : ${errorMessage(e)}`, true);
    } finally {
      setBusy(false);
    }
  };

  const reset = () => run("Réinitialisation", `Réinitialiser le mot de passe de ${user.email} ? Ses sessions ouvertes seront fermées.`, async () => {
    const out = await api<{ temporary_password?: string }>(`/users/${user.id}/reset-password`, { method: "POST" });
    if (out.temporary_password) onSecret({ email: user.email, password: out.temporary_password });
    toast("Mot de passe réinitialisé");
  });
  const toggle = () => run(user.active ? "Désactivation" : "Réactivation",
    user.active ? `Désactiver ${user.email} ? Il sera déconnecté immédiatement et ne pourra plus se connecter.` : null, async () => {
      const out = await api<UserRow>(`/users/${user.id}/${user.active ? "disable" : "enable"}`, { method: "POST" });
      toast(out.active ? "Compte réactivé" : "Compte désactivé · sessions fermées");
      onChanged(out);
    });
  const remove = () => {
    // Compte déjà utilisé : confirmation renforcée (saisie de l'email).
    if (user.last_login_at) {
      const typed = window.prompt(`Suppression définitive et irréversible.\nTapez l'email du compte pour confirmer :\n${user.email}`);
      if (typed == null) return;
      if (typed.trim().toLowerCase() !== user.email.toLowerCase()) {
        toast("Email incorrect : suppression annulée", true);
        return;
      }
    }
    return run("Suppression", user.last_login_at ? null : `Supprimer définitivement ${user.email} ? Cette action est irréversible.`, async () => {
      await api(`/users/${user.id}`, { method: "DELETE" });
      toast("Utilisateur supprimé");
      onChanged(undefined, true);
    });
  };
  const canDelete = !self && (!user.last_login_at || !user.active);

  return (
    <div className="stack" style={{ gap: 10 }}>
      <h3 style={{ margin: 0, fontSize: 15 }}>Accès</h3>
      <div className="row">
        <button className="btn" type="button" disabled={busy} onClick={reset}><Icon name="key" />Réinitialiser le mot de passe</button>
        {!self && (
          <button className={`btn${user.active ? " danger" : ""}`} type="button" disabled={busy} onClick={toggle}>
            <Icon name="power" />{user.active ? "Désactiver" : "Réactiver"}
          </button>
        )}
        {canDelete && (
          <button className="btn danger" type="button" disabled={busy} onClick={remove}><Icon name="trash" />Supprimer définitivement</button>
        )}
      </div>
      <span className="faint" style={{ fontSize: 13 }}>
        {self ? "Vous ne pouvez ni désactiver ni supprimer votre propre compte."
          : !user.last_login_at ? "Compte jamais utilisé : la suppression définitive est possible directement."
          : user.active ? "Ce compte a déjà été utilisé : désactivez-le d'abord ; la suppression définitive deviendra possible."
          : "Compte désactivé : il peut être supprimé définitivement (l'audit conserve une trace du compte)."}
      </span>
    </div>
  );
}

export function UsersPage() {
  const me = useAuth();
  const users = useUsers();
  const roles = useRoles();
  const canWrite = me.can("users.write");
  const [q, setQ] = useState("");
  const [status, setStatus] = useState<Status>("active");
  const [roleFilter, setRoleFilter] = useState("");
  const [editing, setEditing] = useState<Editing>(null);
  const [secret, setSecret] = useState<{ email: string; password: string } | null>(null);

  const all = users.data ?? [];
  const list = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return all.filter((u) =>
      (status === "all" || (status === "active") === u.active)
      && (!roleFilter || u.role === roleFilter)
      && (!needle || `${u.name} ${u.email} ${u.operator_code ?? ""}`.toLowerCase().includes(needle)));
  }, [all, q, status, roleFilter]);

  const roleLabel = (c: string | null) => roles.data?.find((r) => r.code === c)?.label ?? ROLE_LABEL[c ?? ""] ?? c ?? "–";
  const close = () => setEditing(null);

  return (
    <main className="page">
      <PageHead eyebrow="Administration" title="Utilisateurs"
        right={canWrite && <button className="btn primary" type="button" onClick={() => { setSecret(null); setEditing({ mode: "create" }); }}><Icon name="plus" />Nouvel utilisateur</button>} />

      {secret && !editing && <SecretBox {...secret} onClose={() => setSecret(null)} />}

      <div className="kpis">
        <Kpi label="Comptes actifs" value={all.filter((u) => u.active).length} tone="good" />
        <Kpi label="Désactivés" value={all.filter((u) => !u.active).length} />
        <Kpi label="Administrateurs" value={all.filter((u) => u.active && (u.role === "ADMIN" || u.role === "SUPER_ADMIN")).length} tone="info" />
        <Kpi label="Opérateurs" value={all.filter((u) => u.active && u.role === "OPERATOR").length} />
      </div>

      <div className="row between">
        <div className="row">
          <input className="input" type="search" placeholder="Nom, email, code opérateur…" aria-label="Rechercher un utilisateur"
            value={q} onChange={(e) => setQ(e.target.value)} style={{ width: 280 }} />
          <select className="select" aria-label="Filtrer par rôle" value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
            <option value="">Tous les rôles</option>
            {(roles.data ?? []).map((r) => <option key={r.code} value={r.code}>{r.label}</option>)}
          </select>
        </div>
        <Segmented label="Statut" value={status} onChange={setStatus} options={[["active", "Actifs"], ["disabled", "Désactivés"], ["all", "Tous"]]} />
      </div>

      <div className="tile flush">
        {users.isLoading ? <div className="empty"><Spinner /></div> : users.isError ? (
          <Empty title="Chargement impossible">{errorMessage(users.error)}</Empty>
        ) : list.length === 0 ? <Empty title="Aucun utilisateur">Modifiez la recherche ou les filtres.</Empty> : (
          <div className="scroll">
            <table className="table">
              <thead><tr><th>Utilisateur</th><th>Rôle</th><th>Code opérateur</th><th>Statut</th><th>Dernière connexion</th><th className="r"><span className="sr-only">Actions</span></th></tr></thead>
              <tbody>
                {list.map((u) => (
                  <tr key={u.id}>
                    <td><b>{u.name}</b>{u.id === me.user_id && <span className="faint"> (vous)</span>}<div className="muted" style={{ fontSize: 13 }}>{u.email}</div></td>
                    <td><Chip tone={ROLE_TONE[u.role ?? ""] ?? "gray"}>{roleLabel(u.role)}</Chip></td>
                    <td className="mono">{u.operator_code ?? "–"}</td>
                    <td>{u.active ? <Chip tone="green" dot>Actif</Chip> : <Chip tone="gray" dot>Désactivé</Chip>}</td>
                    <td className="muted">{u.last_login_at ? dateTime(u.last_login_at) : "Jamais"}</td>
                    <td className="r">
                      {canWrite && (
                        <button className="btn sm" type="button" onClick={() => { setSecret(null); setEditing({ mode: "edit", user: u }); }}
                          aria-label={`Modifier ${u.email}`}><Icon name="edit" />Modifier</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Drawer open={editing != null} onClose={close}
        title={editing?.mode === "edit" ? editing.user.name : "Nouvel utilisateur"}
        subtitle={editing?.mode === "edit" && <span className="muted">{editing.user.email} · créé le {dateTime(editing.user.created_at)}</span>}>
        {editing && roles.data && (
          <>
            {secret && <SecretBox {...secret} onClose={() => { setSecret(null); if (editing.mode === "create") close(); }} />}
            {!(secret && editing.mode === "create") && (
              <UserForm key={editing.mode === "edit" ? editing.user.id : "new"} editing={editing} roles={roles.data}
                onSecret={setSecret}
                onSaved={(u) => { if (u) setEditing({ mode: "edit", user: u }); }} />
            )}
            {editing.mode === "edit" && (
              <DangerZone user={editing.user} onSecret={setSecret}
                onChanged={(u, closed) => (closed ? close() : u && setEditing({ mode: "edit", user: u }))} />
            )}
          </>
        )}
      </Drawer>
    </main>
  );
}
